import { Store } from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';
import { memo } from 'react';
import { useTranslation } from "react-i18next";

interface ShopStatusSectionProps {
  value: string;
  onChange: (value: string) => void;
}

const statusOptions = [
  {
    value: "WAIT_FOR_REVIEW",
    key: "wait_for_review",
    chipColor: "default" as const,
  },
  {
    value: "REVIEWING",
    key: "reviewing",
    chipColor: "warning" as const,
  },
  {
    value: "ACTIVE",
    key: "active",
    chipColor: "success" as const,
  },
  {
    value: "REJECTED",
    key: "rejected",
    chipColor: "error" as const,
  },
];

const ShopStatusSectionComponent = ({ value, onChange }: ShopStatusSectionProps) => {
  const { t } = useTranslation();
  return (
    <Card elevation={2}>
      <CardHeader
        avatar={<Store sx={{ color: '#ba2156' }} />}
        title={
          <Typography variant="h6" component="h3">
            {t("salon.settings.status.title")}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {t("salon.settings.status.subtitle")}
          </Typography>
        }
      />
      <CardContent>
        <FormControl component="fieldset">
          <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {statusOptions.map((option) => (
                <Box
                  key={option.value}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      value={option.value}
                      control={
                        <Radio 
                          sx={{ 
                            color: '#ba2156', 
                            '&.Mui-checked': { color: '#ba2156' } 
                          }} 
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={t(`salon.settings.status.options.${option.key}.label`)} 
                            size="small" 
                            color={option.chipColor}
                          />
                        </Box>
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    {t(`salon.settings.status.options.${option.key}.description`)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </RadioGroup>
        </FormControl>
      </CardContent>
    </Card>
  );
};

export const ShopStatusSection = memo(ShopStatusSectionComponent);